import type {Instant, UUID} from './common'
import type {EntityLink} from './entity-link'
import type {OwnerResponse} from './owner'

export interface RestrictionRuleVersion {
    id: UUID
    ruleId: UUID
    versionNumber: number
    name: string
    description: string | null
    conditionExpr: string
    removalGraceDays: number
    ownerSnapshot: string
    current: boolean
    createdAt: Instant
    createdBy: EntityLink | null
}

export interface RestrictionRuleVersionPage {
    content: RestrictionRuleVersion[]
    totalElements: number
    totalPages: number
    number: number
    size: number
}

export interface RestrictionRule {
    id: UUID
    name: string
    description: string | null
    conditionExpr: string
    deniedProfileCondition: string | null
    active: boolean
    removalGraceDays: number
    profileCount: number
    openViolationCount: number
    version: number
    createdAt: Instant
    updatedAt: Instant
    owners: OwnerResponse[]
}

export interface RestrictionRulePage {
    content: RestrictionRule[]
    totalElements: number
    totalPages: number
    number: number
    size: number
}

export interface RestrictionRuleProfile {
    id: UUID
    accessProfile: EntityLink
    resource: EntityLink
    targetSystem: EntityLink
}

export interface RestrictionRuleViolation {
    id: UUID
    ruleId: UUID
    ruleName: string
    identity: EntityLink
    violatingProfile: EntityLink
    detectedAt: Instant
    resolvedAt: Instant | null
}

export interface RestrictionRuleViolationPage {
    content: RestrictionRuleViolation[]
    totalElements: number
    totalPages: number
    number: number
    size: number
}

export interface RestrictionRuleCreateRequest {
    name: string
    description?: string
    conditionExpr: string
    ownerPartyId: UUID
    deniedProfileIds?: UUID[]
    deniedProfileCondition?: string
    removalGraceDays?: number
}

export interface RestrictionRuleUpdateRequest {
    name: string
    description?: string
    conditionExpr: string
    ownerPartyId: UUID
    deniedProfileIds?: UUID[]
    deniedProfileCondition?: string
    version: number
    removalGraceDays?: number
}

export interface ReviewRequest {
    comment?: string;
}

/* eslint-disable i18next/no-literal-string -- const definition is the source of truth */
export const RestrictionRuleDetectionStatus = {
    SUCCESS: 'SUCCESS',
    FAILED: 'FAILED',
} as const
/* eslint-enable i18next/no-literal-string */
export type RestrictionRuleDetectionStatus = typeof RestrictionRuleDetectionStatus[keyof typeof RestrictionRuleDetectionStatus]

export interface RestrictionRuleDetectionLog {
    id: UUID
    ruleId: UUID
    ruleName: string
    ruleVersion: number
    detectionStartedAt: Instant
    durationMs: number
    status: RestrictionRuleDetectionStatus
    identitiesEvaluated: number
    violationsFound: number
    violationsResolved: number
    createdAt: Instant
}

export interface RestrictionRuleDetectionLogPage {
    content: RestrictionRuleDetectionLog[]
    totalElements: number
    totalPages: number
    number: number
    size: number
}

/* eslint-disable i18next/no-literal-string -- const definitions and field name constants for backend API */
export const RestrictionRuleStatus = {
    ACTIVE: 'ACTIVE',
    INACTIVE: 'INACTIVE',
} as const
export type RestrictionRuleStatus = typeof RestrictionRuleStatus[keyof typeof RestrictionRuleStatus]


export const RestrictionRuleCreateRequestFields = {
    name: 'name',
    description: 'description',
    conditionExpr: 'conditionExpr',
    ownerPartyId: 'ownerPartyId',
    deniedProfileIds: 'deniedProfileIds',
    deniedProfileCondition: 'deniedProfileCondition',
    removalGraceDays: 'removalGraceDays',
} as const

export const RestrictionRuleProfileResponseFields = {
    id: 'id',
    accessProfile: 'accessProfile',
    resource: 'resource',
    targetSystem: 'targetSystem',
} as const

export const RestrictionRuleResponseFields = {
    id: 'id',
    name: 'name',
    description: 'description',
    conditionExpr: 'conditionExpr',
    deniedProfileCondition: 'deniedProfileCondition',
    active: 'active',
    removalGraceDays: 'removalGraceDays',
    profileCount: 'profileCount',
    openViolationCount: 'openViolationCount',
    version: 'version',
    createdAt: 'createdAt',
    updatedAt: 'updatedAt',
    owners: 'owners',
} as const

export const RestrictionRuleUpdateRequestFields = {
    name: 'name',
    description: 'description',
    conditionExpr: 'conditionExpr',
    ownerPartyId: 'ownerPartyId',
    deniedProfileIds: 'deniedProfileIds',
    deniedProfileCondition: 'deniedProfileCondition',
    version: 'version',
    removalGraceDays: 'removalGraceDays',
} as const

export const RestrictionRuleViolationResponseFields = {
    id: 'id',
    ruleId: 'ruleId',
    ruleName: 'ruleName',
    identity: 'identity',
    violatingProfile: 'violatingProfile',
    detectedAt: 'detectedAt',
    resolvedAt: 'resolvedAt',
} as const

export const ReviewRequestFields = {
    comment: 'comment',
} as const

export const RestrictionRuleVersionResponseFields = {
    id: 'id',
    ruleId: 'ruleId',
    versionNumber: 'versionNumber',
    name: 'name',
    description: 'description',
    conditionExpr: 'conditionExpr',
    removalGraceDays: 'removalGraceDays',
    ownerSnapshot: 'ownerSnapshot',
    current: 'current',
    createdAt: 'createdAt',
    createdBy: 'createdBy',
} as const

export const RestrictionRuleDetectionLogResponseFields = {
    id: 'id',
    ruleId: 'ruleId',
    ruleName: 'ruleName',
    ruleVersion: 'ruleVersion',
    detectionStartedAt: 'detectionStartedAt',
    durationMs: 'durationMs',
    status: 'status',
    identitiesEvaluated: 'identitiesEvaluated',
    violationsFound: 'violationsFound',
    violationsResolved: 'violationsResolved',
    createdAt: 'createdAt',
} as const

export const RestrictionRuleDetectionFilterParams = {
    ruleId: 'ruleId',
} as const

export interface RestrictionRuleTestResultItem {
    identity: EntityLink
    party: EntityLink
    violation: boolean
}

export interface RestrictionRuleTestResult {
    totalEvaluated: number
    matched: number
    violations: number
    items: RestrictionRuleTestResultItem[]
    testId: string
}

export const RestrictionRuleTestResponseFields = {
    totalEvaluated: 'totalEvaluated',
    matched: 'matched',
    items: 'items',
    testId: 'testId',
} as const
/* eslint-enable i18next/no-literal-string */
